import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { it } from 'date-fns/locale';
import { api } from '../../utils/api';

const getItemLabel = (item) => {
  if (item.description) return item.description;
  if (item.Product?.name) return item.Product.name;
  return 'Articolo';
};

const isCustomItem = (item) =>
  item.isCustom ||
  item.Product?.code?.startsWith('ART-CUST') ||
  (!item.productId && !!item.purchasePrice);

const SaleEditDialog = ({
  open,
  sale,
  paymentMethods,
  accounts,
  onClose,
  onSaved
}) => {
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(null);
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !sale) return;

    setItems(
      (sale.items || []).map((item) => ({
        id: item.id,
        productId: item.productId,
        description: getItemLabel(item),
        quantity: item.quantity || 1,
        price: parseFloat(item.price) || 0,
        purchasePrice: parseFloat(item.purchasePrice || item.Product?.purchasePrice) || 0,
        isCustom: isCustomItem(item),
        includeLabor: !!item.includeLabor
      }))
    );
    setNotes(sale.notes || '');
    setPaymentAmount(
      sale.paymentAmount !== undefined && sale.paymentAmount !== null
        ? String(parseFloat(sale.paymentAmount))
        : ''
    );
    setPaymentDate(sale.paymentDate ? new Date(sale.paymentDate) : new Date(sale.createdAt));
    setPaymentMethodId(sale.paymentMethodId || '');
    setAccountId(sale.accountId || '');
    setError('');
  }, [open, sale]);

  const totals = useMemo(() => {
    const subtotal = items.reduce(
      (sum, item) => sum + (parseFloat(item.price) || 0) * (parseInt(item.quantity, 10) || 0),
      0
    );
    return subtotal;
  }, [items]);

  const handleItemChange = (index, field, value) => {
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };

  const handleRemoveItem = (index) => {
    if (items.length <= 1) return;
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setError('');
    setSaving(true);

    try {
      const payload = {
        items: items.map((item) => ({
          id: item.id,
          productId: item.productId,
          description: item.description,
          quantity: parseInt(item.quantity, 10),
          price: parseFloat(item.price),
          purchasePrice: parseFloat(item.purchasePrice) || 0,
          isCustom: item.isCustom,
          includeLabor: item.includeLabor
        })),
        notes,
        paymentAmount: parseFloat(paymentAmount),
        paymentDate: paymentDate
          ? paymentDate.toISOString().split('T')[0]
          : null,
        paymentMethodId: paymentMethodId || null,
        accountId: accountId || null
      };

      const response = await api.put(`/sales/${sale.id}`, payload);
      onSaved(response.data.sale);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nel salvataggio della vendita');
    } finally {
      setSaving(false);
    }
  };

  if (!sale) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Modifica vendita #{sale.id}
        {sale.saleNumber ? ` (${sale.saleNumber})` : ''}
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="subtitle1" gutterBottom>
          Articoli
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Descrizione</TableCell>
                <TableCell align="right" width={100}>Qtà</TableCell>
                <TableCell align="right" width={120}>Prezzo</TableCell>
                {items.some((item) => item.isCustom) && (
                  <TableCell align="right" width={120}>Costo</TableCell>
                )}
                <TableCell align="right" width={120}>Totale</TableCell>
                <TableCell align="center" width={60} />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => {
                const lineTotal = (parseFloat(item.price) || 0) * (parseInt(item.quantity, 10) || 0);
                return (
                  <TableRow key={item.id || `new-${index}`}>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        disabled={!item.isCustom}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                        inputProps={{ min: 1, step: 1 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={item.price}
                        onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </TableCell>
                    {items.some((row) => row.isCustom) && (
                      <TableCell align="right">
                        {item.isCustom ? (
                          <TextField
                            size="small"
                            type="number"
                            value={item.purchasePrice}
                            onChange={(e) => handleItemChange(index, 'purchasePrice', e.target.value)}
                            inputProps={{ min: 0, step: 0.01 }}
                          />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    )}
                    <TableCell align="right">
                      €{lineTotal.toFixed(2)}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={items.length > 1 ? 'Rimuovi riga' : 'Serve almeno un articolo'}>
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={items.length <= 1}
                            onClick={() => handleRemoveItem(index)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Importo pagato"
              type="number"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
              <DatePicker
                label="Data pagamento"
                value={paymentDate}
                onChange={setPaymentDate}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Metodo di pagamento</InputLabel>
              <Select
                value={paymentMethodId}
                label="Metodo di pagamento"
                onChange={(e) => setPaymentMethodId(e.target.value)}
              >
                {paymentMethods.map((method) => (
                  <MenuItem key={method.id} value={method.id}>
                    {method.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Conto</InputLabel>
              <Select
                value={accountId}
                label="Conto"
                onChange={(e) => setAccountId(e.target.value)}
              >
                {accounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Note"
              multiline
              minRows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Totale articoli: €{totals.toFixed(2)}
              </Typography>
              {parseFloat(paymentAmount || 0) < totals && (
                <Typography variant="caption" color="warning.main">
                  L&apos;importo pagato è inferiore al totale articoli (sconto applicato).
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Annulla
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || items.length === 0}>
          {saving ? <CircularProgress size={22} color="inherit" /> : 'Salva modifiche'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaleEditDialog;
